import type { UserRoleType } from "@/models/user-role"

const isAdminTargetRole = (targetRole: UserRoleType): boolean =>
  targetRole === "admin" || targetRole === "admin_reader"

/**
 * Whether the actor may mutate the target user under existing admin users rules.
 * Mirrors UserPolicy#update? / #destroy? for UI gating (edit/delete chrome, edit Page404).
 * Returns false when targetRole is unknown so loading states do not flash mutate actions.
 */
export const canMutateAdminUser = ({
  isAdminWriterUser,
  isOrgAdminUser,
  targetRole,
}: {
  isAdminWriterUser: boolean
  isOrgAdminUser: boolean
  targetRole: UserRoleType | undefined
}): boolean => {
  if (targetRole === undefined) {
    return false
  }

  if (isAdminWriterUser) {
    return true
  }

  if (!isOrgAdminUser) {
    return false
  }

  return !isAdminTargetRole(targetRole)
}

/**
 * Whether the actor may delete the target user.
 * Same mutate rules, plus never allow deleting the signed-in user.
 */
export const canDeleteAdminUser = ({
  actorId,
  isAdminWriterUser,
  isOrgAdminUser,
  targetId,
  targetRole,
}: {
  actorId: string | number | null | undefined
  isAdminWriterUser: boolean
  isOrgAdminUser: boolean
  targetId: string | number | null | undefined
  targetRole: UserRoleType | undefined
}): boolean => {
  if (
    actorId != null &&
    targetId != null &&
    String(actorId) === String(targetId)
  ) {
    return false
  }

  return canMutateAdminUser({
    isAdminWriterUser,
    isOrgAdminUser,
    targetRole,
  })
}
